import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Badge } from '@/components/ui/badge';
import { BarChart3, Settings, Eye } from 'lucide-react';
import type { ChartPreferences, TooltipFormatting, ChartDisplayPreferences } from '@shared/schemas/chartPreferences';
import { defaultChartPreferences } from '@shared/schemas/chartPreferences';

interface ChartPreferencesSettingsProps {
  preferences: ChartPreferences;
  onSave: (preferences: ChartPreferences) => Promise<void>;
  isLoading?: boolean;
  title?: string;
  description?: string;
  showInheritanceInfo?: boolean;
  inheritedFrom?: string;
}

const ChartPreferencesSettings: React.FC<ChartPreferencesSettingsProps> = ({
  preferences,
  onSave,
  isLoading = false,
  title = "Chart Preferences",
  description = "Customize how charts are displayed and formatted",
  showInheritanceInfo = false,
  inheritedFrom,
}) => {
  const [localPreferences, setLocalPreferences] = useState<ChartPreferences>(preferences);
  const [saving, setSaving] = useState(false);

  const handleTooltipFormattingChange = (key: keyof TooltipFormatting, value: any) => {
    setLocalPreferences(prev => ({
      ...prev,
      tooltipFormatting: {
        ...prev.tooltipFormatting,
        [key]: value,
      },
    }));
  };

  const handleDisplayPreferencesChange = (key: keyof ChartDisplayPreferences, value: any) => {
    setLocalPreferences(prev => ({
      ...prev,
      displayPreferences: {
        ...prev.displayPreferences,
        [key]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(localPreferences);
    } catch (error) {
      console.error('Error saving chart preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalPreferences(defaultChartPreferences);
  };

  const hasChanges = JSON.stringify(localPreferences) !== JSON.stringify(preferences);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <CardTitle>{title}</CardTitle>
          </div>
          <CardDescription>{description}</CardDescription>
          {showInheritanceInfo && inheritedFrom && (
            <div className="flex items-center gap-2 pt-2">
              <Badge variant="outline" className="text-xs">
                Inherits from {inheritedFrom}
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tooltip Formatting Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-lg font-medium">Tooltip Formatting</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Control how numerical values are displayed in chart tooltips.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="showLocalizedNumbers">Localized Numbers</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="showLocalizedNumbers"
                    checked={localPreferences.tooltipFormatting.showLocalizedNumbers}
                    onCheckedChange={(checked) => 
                      handleTooltipFormattingChange('showLocalizedNumbers', checked)
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    Use locale-specific number formatting
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="showThousandsSeparator">Thousands Separator</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="showThousandsSeparator"
                    checked={localPreferences.tooltipFormatting.showThousandsSeparator}
                    onCheckedChange={(checked) => 
                      handleTooltipFormattingChange('showThousandsSeparator', checked)
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    Show commas in large numbers
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="decimalPlacesLessThanOne">Decimals (&lt; 1)</Label>
                <Input
                  id="decimalPlacesLessThanOne"
                  type="number"
                  min="0"
                  max="10"
                  value={localPreferences.tooltipFormatting.decimalPlacesLessThanOne}
                  onChange={(e) => 
                    handleTooltipFormattingChange('decimalPlacesLessThanOne', parseInt(e.target.value))
                  }
                />
                <p className="text-xs text-muted-foreground">For values less than 1</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="decimalPlacesOneToThirty">Decimals (1-30)</Label>
                <Input
                  id="decimalPlacesOneToThirty"
                  type="number"
                  min="0"
                  max="10"
                  value={localPreferences.tooltipFormatting.decimalPlacesOneToThirty}
                  onChange={(e) => 
                    handleTooltipFormattingChange('decimalPlacesOneToThirty', parseInt(e.target.value))
                  }
                />
                <p className="text-xs text-muted-foreground">For values 1-30</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="decimalPlacesGreaterThanThirty">Decimals (&gt; 30)</Label>
                <Input
                  id="decimalPlacesGreaterThanThirty"
                  type="number"
                  min="0"
                  max="10"
                  value={localPreferences.tooltipFormatting.decimalPlacesGreaterThanThirty}
                  onChange={(e) => 
                    handleTooltipFormattingChange('decimalPlacesGreaterThanThirty', parseInt(e.target.value))
                  }
                />
                <p className="text-xs text-muted-foreground">For values greater than 30</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currencySymbol">Currency Symbol (Optional)</Label>
                <Input
                  id="currencySymbol"
                  type="text"
                  placeholder="$, €, £, etc."
                  value={localPreferences.tooltipFormatting.currencySymbol || ''}
                  onChange={(e) => 
                    handleTooltipFormattingChange('currencySymbol', e.target.value || undefined)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="percentagePrecision">Percentage Precision</Label>
                <Input
                  id="percentagePrecision"
                  type="number"
                  min="0"
                  max="10"
                  value={localPreferences.tooltipFormatting.percentagePrecision}
                  onChange={(e) => 
                    handleTooltipFormattingChange('percentagePrecision', parseInt(e.target.value))
                  }
                />
              </div>
            </div>
          </div>

          <hr className="border-border" />

          {/* Display Preferences Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-lg font-medium">Display Preferences</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Control the visual appearance and behavior of charts.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="showGridLines">Grid Lines</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="showGridLines"
                    checked={localPreferences.displayPreferences.showGridLines}
                    onCheckedChange={(checked) => 
                      handleDisplayPreferencesChange('showGridLines', checked)
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    Show grid lines on charts
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="showLegend">Legend</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="showLegend"
                    checked={localPreferences.displayPreferences.showLegend}
                    onCheckedChange={(checked) => 
                      handleDisplayPreferencesChange('showLegend', checked)
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    Show chart legend
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="showTooltips">Tooltips</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="showTooltips"
                    checked={localPreferences.displayPreferences.showTooltips}
                    onCheckedChange={(checked) => 
                      handleDisplayPreferencesChange('showTooltips', checked)
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    Show tooltips on hover
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="enableAnimations">Animations</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="enableAnimations"
                    checked={localPreferences.displayPreferences.enableAnimations}
                    onCheckedChange={(checked) => 
                      handleDisplayPreferencesChange('enableAnimations', checked)
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    Enable chart animations
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="chartBackground">Chart Background</Label>
                <Select 
                  value={localPreferences.displayPreferences.chartBackground}
                  onValueChange={(value) => 
                    handleDisplayPreferencesChange('chartBackground', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transparent">Transparent</SelectItem>
                    <SelectItem value="white">White</SelectItem>
                    <SelectItem value="card">Card Background</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {localPreferences.displayPreferences.enableAnimations && (
              <div className="space-y-2">
                <Label htmlFor="animationDuration">Animation Duration (ms)</Label>
                <Input
                  id="animationDuration"
                  type="number"
                  min="0"
                  max="5000"
                  step="100"
                  value={localPreferences.displayPreferences.animationDuration}
                  onChange={(e) => 
                    handleDisplayPreferencesChange('animationDuration', parseInt(e.target.value))
                  }
                />
              </div>
            )}
          </div>

          <hr className="border-border" />

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving || isLoading}
            >
              Reset to Defaults
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving || isLoading}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChartPreferencesSettings; 